"use client";

import { useState, type FormEvent } from "react";
import type { FacturationRule } from "@/prisma/generated/client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  addFacturationRule,
  editFacturationRule,
} from "@/app/lib/actions/facturation-rules";

type FormValues = {
  brandNumericCode: string;
  brandAlphabeticCode: string;
  brandAlphabeticLabel: string;
  billingCode: FacturationRule["billingCode"];
  serviceType: string;
  noticeItemLabel: string;
  articleNumber: string;
  unitCost: string;
  versionNote?: string;
};

const DEFAULT_FORM: FormValues = {
  brandNumericCode: "",
  brandAlphabeticCode: "",
  brandAlphabeticLabel: "",
  billingCode: "FDM",
  serviceType: "",
  noticeItemLabel: "",
  articleNumber: "",
  unitCost: "",
  versionNote: "",
};

function resolveErrorMessage(result: {
  message?: string;
  fieldErrors?: Record<string, string[]> | string;
}) {
  if (result.message) return result.message;
  if (typeof result.fieldErrors === "string") return result.fieldErrors;
  const firstError = Object.values(result.fieldErrors ?? {}).find(
    (messages) => messages && messages.length > 0,
  );
  return firstError?.[0] ?? "Erreur de validation";
}

export default function FacturationRuleForm({
  rule,
  open,
  onClose,
  onSuccess,
}: {
  rule: FacturationRule | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isNew = !rule;
  const [form, setForm] = useState<FormValues>(() =>
    rule
      ? {
          brandNumericCode: rule.brandNumericCode,
          brandAlphabeticCode: rule.brandAlphabeticCode,
          brandAlphabeticLabel: rule.brandAlphabeticLabel,
          billingCode: rule.billingCode,
          serviceType: rule.serviceType,
          noticeItemLabel: rule.noticeItemLabel,
          articleNumber: rule.articleNumber,
          unitCost: rule.unitCost.toString(),
          versionNote: "",
        }
      : DEFAULT_FORM,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const set = (key: keyof FormValues) => (value: unknown) =>
    setForm((previous) => ({ ...previous, [key]: value }) as FormValues);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const payload = {
      ...(isNew ? {} : { id: rule.id }),
      brandNumericCode: form.brandNumericCode.trim(),
      brandAlphabeticCode: form.brandAlphabeticCode.trim().toUpperCase(),
      brandAlphabeticLabel: form.brandAlphabeticLabel.trim().toUpperCase(),
      billingCode: form.billingCode,
      serviceType: form.serviceType.trim(),
      noticeItemLabel: form.noticeItemLabel.trim(),
      articleNumber: form.articleNumber.trim(),
      unitCost: Number(form.unitCost),
      versionNote: form.versionNote || undefined,
      isActive: true,
    };

    const result = isNew
      ? await addFacturationRule(payload)
      : await editFacturationRule(payload);

    setLoading(false);

    if (!result.success) {
      setError(resolveErrorMessage(result));
      return;
    }

    onSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? "Nouvelle règle de facturation"
              : `Modifier — ${rule?.brandAlphabeticCode} · ${rule?.billingCode} · ${rule?.articleNumber}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          ) : null}

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Code marque numérique</Label>
              <Input
                value={form.brandNumericCode}
                onChange={(e) => set("brandNumericCode")(e.target.value)}
                placeholder="1"
                required
                maxLength={1}
                inputMode="numeric"
                className="font-mono tracking-widest"
              />
            </div>

            <div className="space-y-2">
              <Label>Code marque alphabétique</Label>
              <Input
                value={form.brandAlphabeticCode}
                onChange={(e) =>
                  set("brandAlphabeticCode")(e.target.value.toUpperCase())
                }
                placeholder="V"
                required
                maxLength={1}
                className="font-mono tracking-widest uppercase"
              />
            </div>

            <div className="space-y-2">
              <Label>Code facturation</Label>
              <Select
                value={form.billingCode}
                onValueChange={(value) => set("billingCode")(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["FDM", "VN", "FU", "VO", "DA", "CS"] as const).map(
                    (code) => (
                      <SelectItem key={code} value={code}>
                        {code}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Libellé marque alphabétique</Label>
            <Input
              value={form.brandAlphabeticLabel}
              onChange={(e) =>
                set("brandAlphabeticLabel")(e.target.value.toUpperCase())
              }
              placeholder="VOLKSWAGEN"
              required
              maxLength={20}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type de service</Label>
              <Input
                value={form.serviceType}
                onChange={(e) => set("serviceType")(e.target.value)}
                placeholder="EX : IMMATRICULATION"
                required
                maxLength={50}
              />
            </div>

            <div className="space-y-2">
              <Label>Libellé texte poste notice</Label>
              <Input
                value={form.noticeItemLabel}
                onChange={(e) => set("noticeItemLabel")(e.target.value)}
                placeholder="EX : PRESTATION ADMIN"
                required
                maxLength={50}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Numéro d&apos;article</Label>
              <Input
                value={form.articleNumber}
                onChange={(e) => set("articleNumber")(e.target.value)}
                placeholder="123456"
                required
                maxLength={6}
                inputMode="numeric"
                className="font-mono tracking-widest"
              />
            </div>

            <div className="space-y-2">
              <Label>Coût unitaire</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.unitCost}
                onChange={(e) => set("unitCost")(e.target.value)}
                placeholder="0.00"
                required
                inputMode="decimal"
              />
            </div>
          </div>

          {!isNew ? (
            <div className="space-y-2">
              <Label>
                Note de modification{" "}
                <span className="text-muted-foreground font-normal">
                  (optionnel)
                </span>
              </Label>
              <Input
                value={form.versionNote ?? ""}
                onChange={(e) => set("versionNote")(e.target.value)}
                placeholder="Motif de la modification…"
                disabled={loading}
              />
            </div>
          ) : null}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Enregistrement…" : isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
